import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionService, CreditInfo } from '@/services/subscription.service';
import { useAuth } from './AuthContext';

interface CreditContextType {
  creditInfo: CreditInfo | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

export function CreditProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['credits'],
    queryFn: subscriptionService.getCreditInfo,
    // Refresh every 5 minutes
    staleTime: 5 * 60 * 1000,
    // Don't refetch on window focus to reduce API calls
    refetchOnWindowFocus: false,
    // Only fetch if the user is logged in
    enabled: !!user,
    retry: false,
  });
  
  return (
    <CreditContext.Provider 
      value={{ 
        creditInfo: data, 
        isLoading, 
        error: error as Error | null, 
        refetch 
      }}
    >
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
} 